// Core functionality of redux state is tied to these methods and param reassignment.
// Since Redux is basically legacy code at this point we are grandfathering in these files. The rule will be enforced for any new code committed.
/* eslint-disable no-param-reassign */
import { getDefinition } from 'app/support/searchParameterDefinition';

// Some search parameters in our state do not use the same name as our Query class and corresponding query string key.
// For example, our state object has a .sort function on the Immutable.js object that represents searchParameters.
// This makes 'sort' an unusable searchParameter name, since it can cover up that function and lead to subtle bugs.

// query keys that translate very straightforwardly to a corresponding search parameter: new name, same value
const simpleQueryKeyAliases = {
  sort: 'sortorder', // .sort and .sortBy are functions of the Immutable object we use to hold searchParameters, so are not usable
};

// search parameters that translate very straightforwardly to a corresponding query key (same value, key is different than search parameter name)
const simpleSearchParameterNameAliases = {
  sortorder: 'sort',
  analogcollection: 'collections',
};

function cleanSearchParameterValue(searchParameterName, rawValue) {
  if (typeof rawValue === 'object') {
    return rawValue;
  } // don't try to change array/object values, they are likely already correct.
  const searchParameterDefinition = getDefinition(searchParameterName);
  if (!searchParameterDefinition) {
    return rawValue;
  }
  if (rawValue && searchParameterDefinition.type === 'multivalue') {
    return rawValue.split(',');
  }
  if (rawValue && searchParameterDefinition.type === 'boolean') {
    if (
      rawValue &&
      rawValue.toLowerCase &&
      rawValue.toLowerCase() === 'false'
    ) {
      return false;
    }
    if (rawValue && rawValue.toLowerCase && rawValue.toLowerCase() === 'true') {
      return true;
    }
    if (rawValue === '') {
      return false;
    }
    return !!rawValue;
  }
  return rawValue;
}

function cleanParameterMapping(name, rawValue) {
  return {
    name,
    value: cleanSearchParameterValue(name, rawValue),
  };
}

export function fromQueryKey(queryKey) {
  return simpleQueryKeyAliases[queryKey] || queryKey;
}

// Mapping might be more complex than simple one:one relationships, so we should map entire collections.
// Where this is being used, switch to requiring use of a searchParameterName instead of a Query key.
// TODO - return a hash, rather than an array of key/value pairs (more efficient data structure). Update any callers accordingly.
function fromQueryKeyAndValue(queryName, queryValue) {
  // start with special cases, then look for a simpler mapping:
  // 'agreements' collapses all four legacy keys into one comma-joined query
  // value, so unlike a plain 1:1 parameter, dropping one kind out of that
  // string (a toggle-off) doesn't make it disappear from the mapping the way
  // clearing a normal parameter would — the mapping has to say so explicitly
  // for every kind, every time, or the legacy key for whichever kind was
  // toggled off (or the last one cleared, leaving an empty queryValue) never
  // gets reset and the switch reads as stuck on.
  if (queryName === 'agreements') {
    const agreements = queryValue ? queryValue.split(',') : [];
    const findAgreement = (prefix) =>
      agreements.find((agreement) => agreement.indexOf(prefix) > -1);

    return [
      cleanParameterMapping('premiumaccess', findAgreement('pa:')),
      cleanParameterMapping('easyaccess', findAgreement('ea:')),
      cleanParameterMapping('editorialsubscription', findAgreement('ed:')),
      cleanParameterMapping('royaltyfreesubscription', findAgreement('rf:')),
    ];
  }

  // 'color' only ever holds 'bw' or nothing (ColorSearchFilterEnum has no
  // other member), so it always maps to bwonly — including the clear, which
  // must reach 'bwonly' explicitly rather than falling through to the
  // generic mapping below and writing an unrelated 'color' key that nothing
  // reads, leaving the switch stuck on.
  if (queryName === 'color') {
    return [{ name: 'bwonly', value: queryValue === 'bw' ? true : undefined }];
  }
  const searchParameterName = simpleQueryKeyAliases[queryName] || queryName;
  return [cleanParameterMapping(searchParameterName, queryValue)];
}

export function toQueryKeyAndValue(searchParameterName, searchParameterValue) {
  if (searchParameterName === 'premiumaccess') {
    return { key: 'agreements', value: searchParameterValue };
  }
  if (searchParameterName === 'easyaccess') {
    return { key: 'agreements', value: searchParameterValue };
  }
  if (searchParameterName === 'editorialsubscription') {
    return { key: 'agreements', value: searchParameterValue };
  }
  if (searchParameterName === 'royaltyfreesubscription') {
    return { key: 'agreements', value: searchParameterValue };
  }
  if (searchParameterName === 'bwonly' && searchParameterValue) {
    return { key: 'color', value: 'bw' };
  }
  if (searchParameterName === 'istockexclusive') {
    if (searchParameterValue) {
      return { key: 'istockexclusive', value: 'yes' };
    }
  }
  if (simpleSearchParameterNameAliases[searchParameterName]) {
    return {
      key: simpleSearchParameterNameAliases[searchParameterName],
      value: searchParameterValue,
    };
  }
  const definition = getDefinition(searchParameterName);
  if (Array.isArray(searchParameterValue) && definition.type === 'multivalue') {
    searchParameterValue = searchParameterValue.join(',');
  }
  return { key: searchParameterName, value: searchParameterValue };
}

export function fromQueryKeysAndValues(queryValues) {
  const searchParameters = {};
  Object.keys(queryValues).forEach((queryKey) => {
    const queryValue = queryValues[queryKey];
    const mappings = fromQueryKeyAndValue(queryKey, queryValue);
    for (const mapping of mappings) {
      searchParameters[mapping.name] = mapping.value;
    }
  });

  return searchParameters;
}

export function fromMultipleQueryObjects(queryObjects) {
  const searchParameters = {};
  for (const queryValues of queryObjects) {
    const object = fromQueryKeysAndValues(queryValues);
    Object.keys(object).forEach((parameterName) => {
      // "First array element wins" seems more human-friendly than "last one wins", so don't overwrite a searchParameter:
      const parameterValue = object[parameterName];
      if (!Object.keys(searchParameters).includes(parameterName)) {
        searchParameters[parameterName] = parameterValue;
      }
    });
  }

  return searchParameters;
}
